import {
    createSlice,
    createAsyncThunk,
    createEntityAdapter,
} from "@reduxjs/toolkit";
import { request } from "@/utils/api";
import { upsertShipmentsIO } from "./shipmentSlice";
// Remove this import as it can't be used here
// import { useDispatch } from "react-redux";

// Create entity adapter
const orderAdapter = createEntityAdapter({
    // Select MongoDB-style _id as the entity ID
    selectId: (order) => order._id,

    // Optional default sorting
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
});

// Async Thunks
export const fetchOrderData = createAsyncThunk(
    "order/fetch",
    async (data, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/getOrders",
                method: "GET",
                params: data,
            });

            if (!response.success || !Array.isArray(response.data?.orders)) {
                return rejectWithValue(response.error.message || "Failed to fetch orders");
            }

            return response.data.orders;
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch orders");
        }
    }
);
export const ImportOrdersFromChannel = createAsyncThunk(
    "order/fetchFromChannel",
    async (_, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/importOrdersFromChannel",
                method: "POST",
            });

            if (!response.success || !Array.isArray(response.data?.results)) {
                return rejectWithValue(response.error.message || "Failed to import orders");
            }

            const allOrders = response?.data?.results?.flatMap(result => result.orders || []);
            console.log(allOrders);

            return allOrders;
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to import orders");
        }
    }
);

export const createOrder = createAsyncThunk(
    "order/create",
    async (orderData, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/createOrder",
                method: "POST",
                body: orderData,
            });

            if (!response.success || !response.data) {
                return rejectWithValue(response.error.message || "Failed to create order");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to create order");
        }
    }
);

export const updateOrder = createAsyncThunk(
    "order/update",
    async ({ orderData, id }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: `/user/updateOrder`,
                method: "PUT",
                body: orderData,
                params: { id },
            });

            if (!response.success || !response.data) {
                return rejectWithValue(response.error.message || "Failed to update order");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to update order");
        }
    }
);

export const cancelOrder = createAsyncThunk(
    "order/cancel",
    async (orders, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: `/user/cancelOrder`,
                method: "PATCH",
                body: orders,
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to cancel orders");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to cancel orders");
        }
    }
);

export const createBulkOrder = createAsyncThunk(
    "order/createBulk",
    async (bulkData, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/createBulkOrders",
                method: "POST",
                body: bulkData,
            });

            if (!response.success || !Array.isArray(response.data.orders)) {
                return rejectWithValue(response.error || "Failed to create bulk orders");
            }

            return response.data.orders;
        } catch ({ error }) {
            return rejectWithValue(error || "Failed to create bulk orders");
        }
    }
);

export const bookOrders = createAsyncThunk(
    "order/book",
    async (orders, { rejectWithValue, dispatch }) => {
        try {
            const response = await request({
                endpoint: "/user/bookOrders",
                method: "POST",
                body: orders,
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to book orders");
            }

            // Dispatch the shipments action here instead of in the reducer
            const shipments = response.data.details?.successes?.shipments;
            if (shipments && shipments.length > 0) {
                dispatch(upsertShipmentsIO(shipments));
            }
            console.log(response.data)
            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to book orders");
        }
    }
);

// Initial state
const initialState = orderAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const orderSlice = createSlice({
    name: "order",
    initialState,
    reducers: {
        clearOrders: (state) => {
            orderAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
        addAllOrders: (state, action) => {
            // This adds multiple orders into the existing state
            orderAdapter.setAll(state, action.payload);
            // If you want to replace existing orders, you'd use setAll instead
            // orderAdapter.setAll(state, action.payload);
        },
        addOrderIO: (state, action) => {
            // This adds a single order into the existing state
            orderAdapter.addOne(state, action.payload);
        },

        updateOrderIO: (state, action) => {
            // This updates a single order in the existing state
            orderAdapter.upsertOne(state, action.payload);
        },
        deleteOrderIO: (state, action) => {
            // This deletes a single order from the existing state
            orderAdapter.removeOne(state, action.payload);
        },
        createBulkOrderIO: (state, action) => {
            // This creates a single order in the existing state
            orderAdapter.upsertMany(state, action.payload);
        },
        bookOrderIO: (state, action) => {
            const { details } = action.payload || {};
            const { successes, failures } = details || {};
            // Batch update successful orders
            if (successes?.orders?.length) {
                orderAdapter.upsertMany(state, successes.orders);
            }
            // Batch update failed orders
            if (failures?.length) {
                orderAdapter.upsertMany(state, failures);
            }
            // Note: Removed dispatch call since it's not available in reducers
            // Shipments should be handled in the thunk action instead
        },

    },
    extraReducers: (builder) => {
        builder
            // Fetch orders
            .addCase(fetchOrderData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchOrderData.fulfilled, (state, action) => {
                orderAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchOrderData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Create order
            .addCase(createOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                // orderAdapter.addOne(state, action.payload);
                // state.loading = false;
                orderAdapter.upsertOne(state, action.payload);
                //state.ids.unshift(state.ids.pop()); // Move new ID to first position
                state.loading = false;
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Update order
            .addCase(updateOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateOrder.fulfilled, (state, action) => {
                orderAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(updateOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Cancel order (replaces deleteOrder)
            .addCase(cancelOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(cancelOrder.fulfilled, (state, action) => {
                if (Array.isArray(action.payload)) {
                    orderAdapter.upsertMany(state, action.payload);
                } else {
                    orderAdapter.upsertOne(state, action.payload);
                }
                state.loading = false;
            })
            .addCase(cancelOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Create bulk orders
            .addCase(createBulkOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createBulkOrder.fulfilled, (state, action) => {
                orderAdapter.addMany(state, action.payload);
                state.loading = false;
            })
            .addCase(createBulkOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
            })

            // Book orders
            .addCase(bookOrders.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(bookOrders.fulfilled, (state, action) => {
                const successOrders = action.payload.details?.successes?.orders || [];
                const failureOrders = action.payload.details?.failures || [];
                if (successOrders.length > 0) {
                    orderAdapter.upsertMany(state, successOrders);
                    console.log("success orders")
                }

                if (failureOrders.length > 0) {
                    console.log("failure orders")
                    orderAdapter.upsertMany(state, failureOrders);
                }

                // Remove the useDispatch call here
                // The dispatch is now handled in the thunk

                state.loading = false;
            })
            .addCase(bookOrders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Import orders from channel
            .addCase(ImportOrdersFromChannel.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(ImportOrdersFromChannel.fulfilled, (state, action) => {
                orderAdapter.addMany(state, action.payload);
                state.loading = false;
            })
            .addCase(ImportOrdersFromChannel.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
    },
});

// Update selectors export
export const {
    selectAll: selectAllOrders,
    selectById: selectOrderById,
    selectIds: selectOrderIds,
} = orderAdapter.getSelectors((state) => state.order);

// Update actions export
export const { clearOrders, addAllOrders, addOrderIO, bookOrderIO, createBulkOrderIO, createOrderIO, deleteOrderIO, updateOrderIO } = orderSlice.actions;
export default orderSlice.reducer;